/**
 * 전화번호 관련 유틸리티 통합
 * validators와 formatters를 래핑하여 편의성 제공
 */

import {
  isValidMobile,
  isValidKoreanPhone,
} from './validators';
import {
  formatKoreanPhone,
  maskPhone,
  getPhoneType,
} from './formatters';

/**
 * 한국 전화번호 포맷팅
 * @alias formatKoreanPhone
 */
export function formatPhoneNumber(mobileno: string): string {
  return formatKoreanPhone(mobileno);
}

/**
 * 전화번호 유효성 검사
 * @alias isValidKoreanPhone
 */
export function isValidPhoneNumber(phone: string): boolean {
  return isValidKoreanPhone(phone);
}

/**
 * 전화번호 마스킹 (010-1234-5678 → 010-****-5678)
 * @alias maskPhone
 */
export function maskPhoneNumber(phone: string): string {
  return maskPhone(phone);
}

/**
 * 전화번호 부분 마스킹 (010-1234-5678 → 010-1***-5678)
 */
export function partialMaskPhoneNumber(phone: string): string {
  if (!phone) {
    return phone;
  }

  // 유효한 번호인지 먼저 확인
  if (!isValidPhoneNumber(phone)) {
    return phone;
  }

  const formatted = formatPhoneNumber(phone);
  const parts = formatted.split('-');

  if (parts.length === 3 && parts[1]?.length === 4) {
    return `${parts[0]}-${parts[1].substr(0, 1)}***-${parts[2]}`;
  }

  return phone;
}

/**
 * PhoneUtil 클래스 - Backend 호환성을 위한 래퍼
 */
export class PhoneUtil {
  /**
   * 한국 전화번호 포맷팅
   */
  static formatKoreanPhone(phone: string): string {
    return formatKoreanPhone(phone);
  }

  /**
   * 전화번호 정규화 (국제번호 포함 처리)
   */
  static normalizePhone(phone: string): string {
    if (!phone) {
      return '';
    }

    // 국제번호 +82를 제거
    let normalized = phone.replace(/\+82/g, '82');

    // 모든 비숫자 문자 제거
    normalized = normalized.replace(/\D/g, '');

    // 한국 국가코드 82로 시작하는 경우 처리
    if (normalized.startsWith('82') && normalized.length > 2) {
      // 82 다음이 0이면 제거 (예: 821012345678 -> 821012345678, 82010... -> 8210...)
      if (normalized.charAt(2) === '0') {
        normalized = `82${normalized.substring(3)}`;
      }
    }

    return normalized;
  }

  /**
   * 한국 전화번호 유효성 검사
   */
  static validateKoreanPhone(phone: string): boolean {
    return isValidKoreanPhone(phone);
  }

  /**
   * 전화번호 유효성 검사 (기존 함수 호환)
   */
  static isValidPhone(phone: string): boolean {
    return isValidPhoneNumber(phone);
  }

  /**
   * 지역번호 추출
   */
  static extractAreaCode(phone: string): string | null {
    if (!phone) {
      return null;
    }

    const numbers = phone.replace(/\D/g, '');

    // 서울 지역번호
    if (numbers.startsWith('02')) {
      return '02';
    }

    // 3자리 시작 번호 (휴대폰, 지역번호, 070 등)
    if (numbers.length >= 3) {
      const prefix = numbers.substr(0, 3);
      const validPrefixes = [
        '031', '032', '033', // 수도권/강원
        '041', '042', '043', '044', // 충청/세종
        '051', '052', '053', '054', '055', // 경상
        '061', '062', '063', '064', // 전라/제주
        '070', '080', // 인터넷/수신자부담
        '010', '011', '016', '017', '018', '019',
      ];
      if (validPrefixes.includes(prefix)) {
        return prefix;
      }
    }

    return null;
  }

  /**
   * 전화번호 타입 식별
   */
  static getPhoneType(phone: string): 'mobile' | 'landline' | 'unknown' {
    return getPhoneType(phone);
  }

  /**
   * 전화번호 마스킹
   */
  static maskPhone(phone: string): string {
    return maskPhone(phone);
  }

  /**
   * 한국 전화번호 포맷팅 (formatKorean 별칭)
   */
  static formatKorean(phone: string): string {
    return this.formatKoreanPhone(phone);
  }

  /**
   * 한국 전화번호 유효성 검사 (isValidKorean 별칭)
   */
  static isValidKorean(phone: string): boolean {
    return this.validateKoreanPhone(phone);
  }

  /**
   * 전화번호 정규화 (normalize 별칭)
   */
  static normalize(phone: string): string {
    if (!phone) {
      return '';
    }
    return phone.replace(/\D/g, '');
  }

  /**
   * 휴대폰 번호인지 확인
   */
  static isMobile(phone: string): boolean {
    return isValidMobile(phone);
  }

  /**
   * 통신사 정보 반환 (간단한 구현)
   */
  static getCarrier(phone: string): string {
    if (!phone || !this.isMobile(phone)) {
      return '기타';
    }

    const numbers = phone.replace(/\D/g, '');

    if (!numbers.startsWith('010')) {
      return '기타';
    }

    const fourthDigit = numbers.charAt(3);

    // 간단한 통신사 매핑 (실제로는 더 복잡함)
    if (['2', '3', '8', '9'].includes(fourthDigit)) {
      return 'SKT';
    } else if (['0', '1', '6', '7'].includes(fourthDigit)) {
      return 'KT';
    } else if (['4', '5'].includes(fourthDigit)) {
      return 'LGU+';
    }

    return '기타';
  }
}
